import { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Dialog } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

export interface EnsureSaveDialogHandle {
  show: (onProceed: () => void) => void
}

interface EnsureSaveDialogProps {
  onSave: () => Promise<void>
}

const EnsureSaveDialog = ({ onSave }: EnsureSaveDialogProps, ref: Ref<EnsureSaveDialogHandle>) => {
  const [showNavModal, setShowNavModal] = useState(false)
  const pendingNavigationRef = useRef<(() => void) | null>(null)

  useImperativeHandle(ref, () => ({
    show: (onProceed: () => void) => {
      pendingNavigationRef.current = onProceed
      setShowNavModal(true)
    },
  }))

  useEffect(() => {
    if (showNavModal) return

    pendingNavigationRef.current?.()
    pendingNavigationRef.current = null
  }, [showNavModal, pendingNavigationRef])

  const handleSaveAndContinue = async () => {
    await onSave()
    setTimeout(() => {
      setShowNavModal(false)
    }, 500)
  }

  const handleCancel = () => {
    setShowNavModal(false)
    pendingNavigationRef.current = null
  }

  const handleContinueWithoutSaving = () => {
    const navigation = pendingNavigationRef.current
    setShowNavModal(false)
    pendingNavigationRef.current = null
    navigation?.()
  }

  return (
    <Dialog
      open={showNavModal}
      onClose={handleCancel}
      header={{
        type: 'critical',
        title: i18n.t('forms.alert_dialog.title'),
        description: '',
      }}
      actions={{
        primary: {
          label: i18n.t('forms.alert_dialog.save_and_continue'),
          onClick: handleSaveAndContinue,
        },
        secondary: {
          label: i18n.t('forms.alert_dialog.continue_without_saving'),
          onClick: handleContinueWithoutSaving,
        },
      }}
    />
  )
}

export default forwardRef(EnsureSaveDialog)
